import React, { useState, useEffect, useCallback, useRef } from "react";
import testimonialsData from "../data/barbequeen-testimonials.json";

const testimonials = testimonialsData.section.testimonials;

/* ─── Side images that rotate with the carousel ───────────────── */
const SIDE_IMAGES = [
  {
    left:  "https://barbequeen.com/public/uploads/thumbnail/thumb_06-13-2025-1749787978.webp",
    right: "https://barbequeen.com/public/uploads/thumbnail/thumb_06-13-2025-1749787571.webp",
  },
  {
    left:  "https://barbequeen.com/public/uploads/home_page/special-06-12-2025-1749716254.webp",
    right: "https://barbequeen.com/public/uploads/thumbnail/thumb_06-12-2025-1749737242.webp",
  },
  {
    left:  "https://barbequeen.com/public/uploads/thumbnail/thumb_06-13-2025-1749787928.webp",
    right: "https://barbequeen.com/public/uploads/thumbnail/thumb_06-13-2025-1749787661.webp",
  },
];

/* ─── Star Rating ─────────────────────────────────────────────── */
const StarRating = ({ count = 5 }) => (
  <div className="flex gap-1 mb-4">
    {Array.from({ length: 5 }).map((_, i) => (
      <svg
        key={i}
        className={`w-4 h-4 ${i < count ? "text-amber-400" : "text-gray-300"}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ))}
  </div>
);

/* ─── Initials Avatar ─────────────────────────────────────────── */
const AVATAR_COLORS = [
  "bg-[#9b1c2e]", "bg-[#c0392b]", "bg-[#7d1624]",
  "bg-[#b5451b]", "bg-[#8e2d1a]", "bg-[#a83232]",
];
const InitialsAvatar = ({ name, index }) => {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div
      className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-[12px] font-bold flex-shrink-0 border-2 border-white shadow-sm ${AVATAR_COLORS[index % AVATAR_COLORS.length]}`}
    >
      {initials}
    </div>
  );
};

/* ─── Single Testimonial Card ─────────────────────────────────── */
const TestimonialCard = ({ item, cardIndex, animKey }) => (
  <div
    key={animKey}
    className="bg-[#f5ede4] rounded-sm px-7 py-7 relative overflow-hidden animate-fade-in-up"
  >
    {/* Stars */}
    <StarRating count={item.rating} />

    {/* Review text */}
    <p className="font-sans text-[13px] text-gray-700 leading-relaxed mb-6 line-clamp-4">
      {item.text}
    </p>

    {/* Author row */}
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <InitialsAvatar name={item.author} index={cardIndex} />
        <div>
          <p className="text-[13px] font-bold text-gray-900 tracking-wide">
            {item.author}
          </p>
          <p className="font-sans text-[10px] text-gray-500 tracking-[0.2em] uppercase font-semibold">
            {item.branch}
          </p>
        </div>
      </div>
      {/* Quote mark */}
      <div className="text-[#9b1c2e] text-5xl font-serif leading-none select-none opacity-80">
        "
      </div>
    </div>
  </div>
);

/* ─── Dot ─────────────────────────────────────────────────────── */
const Dot = ({ active, onClick }) => (
  <button
    onClick={onClick}
    className={`transition-all duration-300 rounded-full ${
      active ? "w-6 h-2 bg-[#9b1c2e]" : "w-2 h-2 bg-gray-300 hover:bg-gray-400"
    }`}
  />
);

/* ─── Main Section ────────────────────────────────────────────── */
const TestimonialsSection = () => {
  /* Show 2 testimonials at a time */
  const PAIR = 2;
  const pairs = Math.ceil(testimonials.length / PAIR);

  const [page,      setPage]      = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const timerRef = useRef(null);

  const maxPage = pairs - 1;

  /* Pair-slice for current page */
  const start   = page * PAIR;
  const visible = testimonials.slice(start, start + PAIR);

  /* Side image set cycles with pairs */
  const sideIdx  = page % SIDE_IMAGES.length;
  const sideImgs = SIDE_IMAGES[sideIdx];

  const stopTimer  = useCallback(() => clearInterval(timerRef.current), []);
  const startTimer = useCallback(() => {
    stopTimer();
    timerRef.current = setInterval(
      () => setPage((p) => (p >= maxPage ? 0 : p + 1)),
      4500
    );
  }, [maxPage, stopTimer]);

  useEffect(() => {
    if (!isHovered) startTimer();
    else stopTimer();
    return stopTimer;
  }, [isHovered, startTimer, stopTimer]);

  const goTo = (idx) => { setPage(idx); startTimer(); };
  const prev = () => goTo(page > 0 ? page - 1 : maxPage);
  const next = () => goTo(page < maxPage ? page + 1 : 0);

  return (
    <section
      id="testimonials"
      className="bg-white py-16 lg:py-24"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="max-w-[1280px] mx-auto px-4 lg:px-8">

        {/* ── Header ── */}
        <div className="text-center mb-12">
          <p className="font-sans text-[10px] font-bold tracking-[0.35em] uppercase text-[#9b1c2e] mb-4">
            Testimonials
          </p>
          <h2 className="text-[32px] sm:text-[42px] lg:text-[50px] font-bold text-gray-900 uppercase leading-tight tracking-wide">
            What Our Guests<br />Are Saying
          </h2>
        </div>

        {/* ── 3-Column Layout ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr_1fr] gap-5 items-stretch min-h-[480px]">

          {/* Left Image */}
          <div className="hidden lg:block relative rounded-sm overflow-hidden">
            <img
              src={sideImgs.left}
              alt="Barbequeen dining"
              key={`left-${page}`}
              className="absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 animate-fade-in"
              onError={(e) => {
                e.target.src = "https://barbequeen.com/public/uploads/thumbnail/thumb_06-13-2025-1749787978.webp";
              }}
            />
          </div>

          {/* Center — 2 stacked cards + controls */}
          <div className="flex flex-col gap-5">
            {visible.map((item, i) => (
              <TestimonialCard
                key={item.id}
                item={item}
                cardIndex={page * 2 + i}
                animKey={`${page}-${i}`}
              />
            ))}

            {/* Navigation */}
            <div className="flex items-center justify-between pt-2">
              {/* Dots */}
              <div className="flex items-center gap-2">
                {Array.from({ length: pairs }).map((_, i) => (
                  <Dot key={i} active={i === page} onClick={() => goTo(i)} />
                ))}
              </div>

              {/* Arrows */}
              <div className="flex gap-2">
                <button
                  onClick={prev}
                  aria-label="Previous testimonials"
                  className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-[#9b1c2e] hover:text-white hover:border-[#9b1c2e] transition-all duration-200"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={next}
                  aria-label="Next testimonials"
                  className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-[#9b1c2e] hover:text-white hover:border-[#9b1c2e] transition-all duration-200"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div className="hidden lg:block relative rounded-sm overflow-hidden">
            <img
              src={sideImgs.right}
              alt="Barbequeen food"
              key={`right-${page}`}
              className="absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 animate-fade-in"
              onError={(e) => {
                e.target.src = "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=600&auto=format&fit=crop";
              }}
            />
            {/* Subtle dark overlay for depth */}
            <div className="absolute inset-0 bg-black/10" />
          </div>

        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;

